import { useState, useMemo } from 'react';

export interface PaginationOptions {
  defaultPageSize?: number;
  defaultPage?: number;
}

export interface PaginationResult<T> {
  // Data
  currentPageData: T[];

  // State
  currentPage: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;

  // Actions
  setCurrentPage: (page: number) => void;
  setPageSize: (size: number) => void;
  goToFirstPage: () => void;
  goToLastPage: () => void;
  goToNextPage: () => void;
  goToPreviousPage: () => void;

  // Computed
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  startIndex: number;
  endIndex: number;
}

export const usePagination = <T>(
  data: T[],
  options: PaginationOptions = {}
): PaginationResult<T> => {
  const { defaultPageSize = 25, defaultPage = 1 } = options;

  const [currentPage, setCurrentPage] = useState(defaultPage);
  const [pageSize, setPageSize] = useState(defaultPageSize);

  const totalItems = data.length;
  const totalPages = Math.ceil(totalItems / pageSize);

  // Ensure current page is valid when data or page size changes
  const validatedCurrentPage = useMemo(() => {
    if (totalPages === 0) return 1;
    return Math.min(Math.max(1, currentPage), totalPages);
  }, [currentPage, totalPages]);

  // Calculate start and end indices
  const startIndex = (validatedCurrentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalItems);

  // Get current page data
  const currentPageData = useMemo(() => {
    return data.slice(startIndex, endIndex);
  }, [data, startIndex, endIndex]);

  // Navigation flags
  const hasNextPage = validatedCurrentPage < totalPages;
  const hasPreviousPage = validatedCurrentPage > 1;

  // Navigation functions
  const goToFirstPage = () => setCurrentPage(1);
  const goToLastPage = () => setCurrentPage(totalPages);
  const goToNextPage = () => {
    if (hasNextPage) setCurrentPage(validatedCurrentPage + 1);
  };
  const goToPreviousPage = () => {
    if (hasPreviousPage) setCurrentPage(validatedCurrentPage - 1);
  };

  // Handle page size change
  const handleSetPageSize = (size: number) => {
    setPageSize(size);
    // Try to maintain current position in data when changing page size
    const currentStartIndex = (validatedCurrentPage - 1) * pageSize;
    const newPage = Math.floor(currentStartIndex / size) + 1;
    setCurrentPage(newPage);
  };

  // Update current page to validated value
  if (currentPage !== validatedCurrentPage) {
    setCurrentPage(validatedCurrentPage);
  }

  return {
    // Data
    currentPageData,

    // State
    currentPage: validatedCurrentPage,
    pageSize,
    totalItems,
    totalPages,

    // Actions
    setCurrentPage,
    setPageSize: handleSetPageSize,
    goToFirstPage,
    goToLastPage,
    goToNextPage,
    goToPreviousPage,

    // Computed
    hasNextPage,
    hasPreviousPage,
    startIndex,
    endIndex,
  };
};
